import { useState, useRef, useEffect } from "react";

const SYSTEM_PROMPT = `You are a Binary Game Facilitator.

Your role is to help users convert vague problems into clear decisions using a structured method called Binary Game.

FORMATTING RULES (CRITICAL):
Do not use markdown formatting. No asterisks, no hashtags, no backticks. Write in plain text only.
Do not use em dashes. Use colons or periods instead.
Keep responses concise. Use line breaks for readability.

CONVERSATION FLOW:

Phase 1: Problem Identification
Ask the user to identify where tension exists. Do not move forward until a clear problem area is identified.

Phase 2: Decision Extraction  
Most users bring problems, not decisions. Help them identify the decision hidden inside the problem. The decision must always become binary (e.g., Stay vs leave, Invest vs wait, Confront vs avoid).

Phase 3: Gate Sequence
Once the decision is clear, run through the gates IN STRICT SEQUENTIAL ORDER.

CRITICAL GATE RULES:
1. Gates must be asked in order: Gate 1, then Gate 2, then Gate 3, and so on.
2. Never skip a gate. Never go back to a previous gate.
3. Ask ONE gate at a time. Wait for the user's answer before moving to the next gate.
4. If the user's answer is unclear, ask them to clarify their answer to the CURRENT gate. Do not move forward until you have a clear yes or no.
5. Once a gate is answered, it is DONE. Never re-ask a completed gate.

THE SEVEN GATES (ask in this exact order):

Gate 1: Meta Gate
Is this a decision you actually control? Do you want clarity right now? Would clarity change your behavior?
If no to any of these, stop the game.

Gate 2: Safety Gate
Would acting on this put you in physical or emotional danger? Would this destabilize your life in 30 days?
If unsafe, stop the game.

Gate 3: Truth Gate
Does this option feel true to you? If nobody else had an opinion, would you still want this?

Gate 4: Evidence Gate
Is there real-world evidence this could work? Have you tested anything like this?

Gate 5: Impact Gate
Has this already cost you time, money, or energy? If nothing changes for a year, will things get worse?

Gate 6: Capability Gate
Do you have the resources to test this? Could you take a small step safely?

Gate 7: Commitment Gate
Are you willing to act on the answer? Are you committed to solving this?

Phase 4: Decision Rule
After Gate 7, deliver the decision rule in this format:
IF [conditions met] THEN [action or non-action]

Every game ends with one of four outcomes:
1. Clear action
2. Clear non-action
3. Pause for evidence
4. Decision not ready

Phase 5: Tree Save Offer
After delivering the decision rule, offer to save the tree. Include the TREE_DATA block at the very end of your response.

Example conclusion message:
"Your decision rule is clear: IF you have the resources and commitment THEN move forward with the hire.

I have organized this into a 5-gate decision tree. Want me to save it to your dashboard so you can reference it later?"

Then append the JSON block:

---TREE_DATA_START---
{
  "title": "Should I [action verb] [subject]?",
  "gates": [
    {
      "id": "01",
      "type": "YES/NO",
      "question": "The exact question from Gate 1",
      "noText": "Exit text if they answer no"
    },
    {
      "id": "02",
      "type": "YES/NO", 
      "question": "The exact question from Gate 2",
      "noText": "Exit text if they answer no"
    }
  ],
  "result_text": "The final decision rule"
}
---TREE_DATA_END---

Gate types in JSON:
Use "YES/NO" for all gates unless it was a choice between two options, then use "THIS/THAT" with yesLabel and noLabel.

The title should be a clear question format like "Should I [verb] [object]?"
Extract the gates that were actually discussed (typically 5-7).
The result_text is the final decision rule you gave them.

IMPORTANT: Only include the TREE_DATA block when you have completed all gates and delivered the decision rule. Do not include it during the normal gate sequence.`;

const DiamondBlue = ({ size = "sm" }) => {
  const sizes = { sm: [5, 8], md: [7, 11], lg: [11, 18], hero: [22, 35] };
  const [w, h] = sizes[size];
  return (
    <svg width={w} height={h} viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}>
      <polygon points="5,0 10,8 5,16 0,8" fill="#0057FF" />
    </svg>
  );
};

const DiamondWhite = ({ size = "sm" }) => {
  const sizes = { sm: [5, 8], md: [7, 11] };
  const [w, h] = sizes[size];
  return (
    <svg width={w} height={h} viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: "inline-block", verticalAlign: "middle", flexShrink: 0 }}>
      <polygon points="5,0 10,8 5,16 0,8" fill="#FFFFFF" />
    </svg>
  );
};

const GATES = ["META", "SAFETY", "TRUTH", "EVIDENCE", "IMPACT", "CAPABILITY", "COMMIT"];

function GateTracker({ activeGate }) {
  return (
    <div className="bg-gate-tracker" style={{
      display: "flex",
      alignItems: "center",
      padding: "20px 24px",
      borderBottom: "0.5px solid #DDDDDD",
      overflowX: "auto",
      background: "#FAFAF8",
      gap: 0,
    }}>
      {GATES.map((gate, i) => {
        const isActive = activeGate === i;
        const isPast = activeGate > i;
        return (
          <div key={gate} style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{
                width: "6px", height: "6px", borderRadius: "50%",
                background: isActive ? "#0057FF" : isPast ? "#0057FF" : "#DDDDDD",
                opacity: isPast ? 0.3 : 1,
                transition: "all 0.3s ease",
                flexShrink: 0,
              }} />
              <span style={{
                fontFamily: "'Geist Mono', 'IBM Plex Mono', monospace",
                fontSize: "9px",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: isActive ? "#0057FF" : isPast ? "#BBBBBB" : "#CCCCCC",
                fontWeight: isActive ? "700" : "400",
                transition: "color 0.3s ease",
              }}>
                {gate}
              </span>
            </div>
            {i < GATES.length - 1 && (
              <div style={{ width: "12px", height: "0.5px", background: "#E0E0E0", margin: "0 4px", flexShrink: 0 }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function GateCard({ msg }) {
  return (
    <div className="bg-gate-card" style={{
      display: "flex",
      background: "#FAFAF8",
      border: "0.5px solid #DDDDDD",
      marginBottom: "16px",
      animation: "fadeSlideIn 0.2s ease-out forwards",
      maxWidth: "100%",
    }}>
      <div style={{ width: "3px", background: "#0057FF", flexShrink: 0, alignSelf: "stretch" }} />
      <div style={{ padding: "16px", flex: 1, minWidth: 0 }}>
        <span style={{
          fontFamily: "'Geist Mono', 'IBM Plex Mono', monospace",
          fontSize: "6.5px",
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          color: "#888888",
          display: "block",
          marginBottom: "10px",
        }}>
          BINARY GAME | FACILITATOR
        </span>
        <p style={{
          fontFamily: "'Work Sans', sans-serif",
          fontWeight: "400",
          fontSize: "15px",
          lineHeight: "1.65",
          color: "#1A1A1A",
          margin: 0,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}>
          {msg.content}
        </p>
      </div>
    </div>
  );
}

function UserCard({ msg }) {
  return (
    <div className="bg-user-card" style={{
      display: "flex",
      justifyContent: "flex-end",
      marginBottom: "16px",
      animation: "fadeSlideIn 0.2s ease-out forwards",
    }}>
      <div style={{
        maxWidth: "85%",
        background: "#FFFFFF",
        border: "0.5px solid #DDDDDD",
        borderRight: "3px solid #0F0F0F",
      }}>
        <div style={{ padding: "12px 16px" }}>
          <span style={{
            fontFamily: "'Geist Mono', 'IBM Plex Mono', monospace",
            fontSize: "6.5px",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            color: "#888888",
            display: "block",
            marginBottom: "8px",
          }}>
            YOU
          </span>
          <p style={{
            fontFamily: "'Work Sans', sans-serif",
            fontWeight: "400",
            fontSize: "15px",
            lineHeight: "1.65",
            color: "#1A1A1A",
            margin: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}>
            {msg.content}
          </p>
        </div>
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div style={{
      display: "flex",
      background: "#FAFAF8",
      border: "0.5px solid #DDDDDD",
      marginBottom: "16px",
    }}>
      <div style={{ width: "3px", background: "#0057FF", flexShrink: 0 }} />
      <div style={{ padding: "16px", display: "flex", alignItems: "center", gap: "5px" }}>
        {[0, 1, 2].map(i => (
          <div key={i} style={{
            width: "4px", height: "4px", borderRadius: "50%",
            background: "#0057FF",
            animation: "bounce 1.2s ease infinite",
            animationDelay: `${i * 0.2}s`,
          }} />
        ))}
      </div>
    </div>
  );
}

function ResultCard({ visible }) {
  if (!visible) return null;
  return (
    <div style={{
      background: "#0F0F0F",
      border: "1.5px solid #0057FF",
      padding: "24px",
      marginTop: "8px",
      marginBottom: "16px",
      animation: "fadeSlideIn 0.2s ease-out forwards",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
        <DiamondBlue size="lg" />
        <span style={{
          fontFamily: "'Geist Mono', 'IBM Plex Mono', monospace",
          fontSize: "6.5px",
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          color: "#AACCFF",
        }}>
          SESSION COMPLETE
        </span>
      </div>
      <p style={{
        fontFamily: "'Work Sans', sans-serif",
        fontWeight: "400",
        fontSize: "13px",
        lineHeight: "1.65",
        color: "#FFFFFF",
        margin: 0,
      }}>
        Your decision rule has been built. The clarity you reached in this session is yours to act on. Start a new session anytime a new tension surfaces.
      </p>
    </div>
  );
}

export default function BinaryGameSession() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeGate, setActiveGate] = useState(-1);
  const [started, setStarted] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [pendingTreeData, setPendingTreeData] = useState(null);
  const [savingTree, setSavingTree] = useState(false);
  const [treeSaved, setTreeSaved] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Parse tree data from AI response
  const parseTreeData = (text) => {
    const startMarker = "---TREE_DATA_START---";
    const endMarker = "---TREE_DATA_END---";
    
    const startIndex = text.indexOf(startMarker);
    const endIndex = text.indexOf(endMarker);
    
    if (startIndex === -1 || endIndex === -1) return null;
    
    const jsonStr = text.substring(startIndex + startMarker.length, endIndex).trim();
    try {
      return JSON.parse(jsonStr);
    } catch (e) {
      console.error("Failed to parse tree data:", e);
      return null;
    }
  };

  // Remove tree data from display text
  const cleanMessageText = (text) => {
    const startMarker = "---TREE_DATA_START---";
    const endMarker = "---TREE_DATA_END---";
    
    const startIndex = text.indexOf(startMarker);
    const endIndex = text.indexOf(endMarker);
    
    if (startIndex === -1 || endIndex === -1) return text;
    
    return text.substring(0, startIndex).trim();
  };

  // Detect if message contains save offer
  const detectSaveOffer = (text) => {
    const lower = text.toLowerCase();
    return (lower.includes("save it to your dashboard") || 
            lower.includes("save it to the dashboard") ||
            lower.includes("-gate decision tree")) &&
           text.includes("---TREE_DATA_START---");
  };

  // Save tree to backend
  const saveTree = async (treeData) => {
    setSavingTree(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/trees`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: treeData.title,
          subtitle: `AI-FACILITATED SESSION // ${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase()}`,
          gates: treeData.gates,
          result_text: treeData.result_text
        }),
      });
      
      if (response.ok) {
        setTreeSaved(true);
        setPendingTreeData(null);
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: "Done. Your decision tree has been saved to your dashboard. You can walk through it anytime." 
        }]);
      } else {
        throw new Error("Failed to save");
      }
    } catch (e) {
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: "There was an issue saving the tree. The session is still complete, but the tree wasn't saved." 
      }]);
    } finally {
      setSavingTree(false);
    }
  };

  const detectGate = (text) => {
    const lower = text.toLowerCase();
    // Detect gate by number or key phrases
    if (lower.includes("gate 1") || lower.includes("meta gate") || lower.includes("do you actually control") || lower.includes("would clarity change")) return 0;
    if (lower.includes("gate 2") || lower.includes("safety gate") || lower.includes("physical or emotional danger") || lower.includes("destabilize")) return 1;
    if (lower.includes("gate 3") || lower.includes("truth gate") || lower.includes("feel true") || lower.includes("nobody else had an opinion")) return 2;
    if (lower.includes("gate 4") || lower.includes("evidence gate") || lower.includes("real-world evidence") || lower.includes("have you tested")) return 3;
    if (lower.includes("gate 5") || lower.includes("impact gate") || lower.includes("cost you time") || lower.includes("get worse")) return 4;
    if (lower.includes("gate 6") || lower.includes("capability gate") || lower.includes("resources to test") || lower.includes("small step safely")) return 5;
    if (lower.includes("gate 7") || lower.includes("commitment gate") || lower.includes("willing to act") || lower.includes("committed to solving")) return 6;
    return -2;
  };

  const detectResult = (text) => {
    const lower = text.toLowerCase();
    return (lower.includes("decision rule") && (lower.includes("then") || lower.includes("action"))) || 
           lower.includes("clear action") || 
           lower.includes("clear non-action") || 
           lower.includes("pause for evidence") || 
           lower.includes("decision not ready") ||
           lower.includes("save it to your dashboard");
  };

  const sendMessage = async (userText) => {
    if (!userText.trim() || loading) return;
    
    // Check if user is responding to save offer
    if (pendingTreeData) {
      const lower = userText.toLowerCase().trim();
      if (lower === "yes" || lower === "yes." || lower === "yeah" || lower === "sure" || lower === "ok" || lower === "okay" || lower.includes("save it") || lower.includes("yes please")) {
        setMessages(prev => [...prev, { role: "user", content: userText }]);
        await saveTree(pendingTreeData);
        return;
      } else if (lower === "no" || lower === "no." || lower === "nope" || lower === "no thanks" || lower.includes("don't save")) {
        setMessages(prev => [...prev, { role: "user", content: userText }]);
        setMessages(prev => [...prev, { role: "assistant", content: "No problem. Your session is complete." }]);
        setPendingTreeData(null);
        return;
      }
    }
    
    const newUserMsg = { role: "user", content: userText };
    const updatedMessages = [...messages, newUserMsg];
    setMessages(updatedMessages);
    setInput("");
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/anthropic`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 4000,
          system: SYSTEM_PROMPT,
          messages: updatedMessages.map(m => ({ role: m.role, content: m.content })),
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error("API error:", errorData);
        throw new Error(errorData.error || "API request failed");
      }
      
      const data = await response.json();
      let assistantText = data.content?.find(b => b.type === "text")?.text;
      
      if (!assistantText) {
        console.error("No text in response:", data);
        throw new Error("No response text received");
      }
      
      // Check for tree data in response
      const treeData = parseTreeData(assistantText);
      if (treeData) {
        setPendingTreeData(treeData);
        assistantText = cleanMessageText(assistantText);
        setShowResult(true);
        setActiveGate(7);
      } else {
        // Only update gate tracker if no tree data (not at conclusion)
        const gateIndex = detectGate(assistantText);
        if (gateIndex >= 0) setActiveGate(gateIndex);
        if (detectResult(assistantText)) { setActiveGate(7); setShowResult(true); }
      }
      
      setMessages(prev => [...prev, { role: "assistant", content: assistantText }]);
    } catch (error) {
      console.error("Send message error:", error);
      // More specific error message that doesn't restart the session
      if (pendingTreeData) {
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: "There was a problem processing the response. Your session is complete. Want me to try saving the tree again? Just say yes." 
        }]);
      } else {
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: "Connection error. Please try sending your message again." 
        }]);
      }
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleStart = async () => {
    setStarted(true);
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/anthropic`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          system: SYSTEM_PROMPT,
          messages: [{ role: "user", content: "Begin the session. Greet me briefly and ask where tension exists right now. Remember: no markdown, no em dashes." }],
        }),
      });
      const data = await response.json();
      const text = data.content?.find(b => b.type === "text")?.text || "Let's begin.\n\nWhere is tension showing up for you right now?";
      setMessages([{ role: "assistant", content: text }]);
    } catch {
      setMessages([{ role: "assistant", content: "Let's begin.\n\nWhere is tension showing up for you right now?" }]);
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleReset = () => {
    setStarted(false); setMessages([]); setActiveGate(-1); setShowResult(false); setInput(""); 
    setPendingTreeData(null); setTreeSaved(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@700&family=Work+Sans:wght@400;700&family=Geist+Mono:wght@400;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { background: #FFFFFF; color: #0F0F0F; font-family: 'Work Sans', sans-serif; -webkit-font-smoothing: antialiased; overflow-x: hidden; width: 100%; max-width: 100vw; }
        @keyframes fadeSlideIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes bounce { 0%,80%,100% { transform: translateY(0); } 40% { transform: translateY(-5px); } }
        textarea:focus { outline: none; }
        textarea { resize: none; }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-track { background: #F0F0EE; }
        ::-webkit-scrollbar-thumb { background: #DDDDDD; }
        
        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .bg-landing { padding: 40px 20px !important; }
          .bg-landing h1 { font-size: 32px !important; }
          .bg-landing .ghost { font-size: 100px !important; left: 6px !important; }
          .bg-cta-grid { grid-template-columns: 1fr !important; }
          .bg-cta-secondary { border-left: 0.5px solid #DDDDDD !important; border-top: none !important; }
          .bg-session { max-width: 100% !important; width: 100% !important; }
          .bg-session-header { padding: 12px 16px !important; flex-wrap: wrap; gap: 8px; }
          .bg-gate-tracker { padding: 12px 16px !important; gap: 4px !important; overflow-x: auto; }
          .bg-gate-tracker span { font-size: 8px !important; }
          .bg-messages { padding: 20px 16px 16px !important; }
          .bg-gate-card { max-width: 100% !important; }
          .bg-user-card { max-width: 85% !important; }
          .bg-input-area { padding: 10px 16px !important; }
          .bg-input-hint { padding: 0 16px 10px !important; }
        }
      `}</style>

      <div style={{ minHeight: "100vh", background: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", width: "100%", maxWidth: "100vw", overflowX: "hidden" }}>

        {!started ? (
          /* LANDING */
          <div className="bg-landing" style={{ maxWidth: "640px", width: "100%", padding: "80px 40px", position: "relative", overflow: "hidden", boxSizing: "border-box" }}>
            {/* Ghost number */}
            <div className="ghost" style={{
              fontFamily: "'Outfit', sans-serif", fontWeight: "700", fontSize: "220px",
              color: "#E6E6E2", position: "absolute", left: "-14px", top: "-20px",
              lineHeight: "1", pointerEvents: "none", userSelect: "none", zIndex: 0,
            }}>01</div>

            <div style={{ position: "relative", zIndex: 1 }}>
              <hr style={{ border: "none", borderTop: "1px solid #0F0F0F", marginBottom: "32px" }} />

              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "64px" }}>
                <DiamondBlue size="md" />
                <span style={{ fontFamily: "'Geist Mono', 'IBM Plex Mono', monospace", fontSize: "6.5px", textTransform: "uppercase", letterSpacing: "0.1em", color: "#888888" }}>
                  BINARY GAME
                </span>
              </div>

              <h1 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: "700", fontSize: "52px", lineHeight: "1.1", letterSpacing: "-0.01em", color: "#0F0F0F", marginBottom: "20px" }}>
                Decision<br />Clarity Protocol
              </h1>

              <p style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: "400", fontSize: "17px", lineHeight: "1.65", color: "#444444", marginBottom: "12px", maxWidth: "100%" }}>
                You don't need more advice.
              </p>
              <p style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: "400", fontSize: "13px", lineHeight: "1.65", color: "#888888", marginBottom: "48px", maxWidth: "100%" }}>
                Binary Game converts vague tension into structured decisions through a sequence of binary gates. Every session ends with a clear rule.
              </p>

              {/* CTA */}
              <div className="bg-cta-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
                <button
                  onClick={handleStart}
                  style={{ background: "#0057FF", color: "#FFFFFF", border: "none", padding: "20px 24px", display: "flex", flexDirection: "column", gap: "6px", cursor: "pointer", textAlign: "left" }}
                  onMouseOver={e => e.currentTarget.style.background = "#0044DD"}
                  onMouseOut={e => e.currentTarget.style.background = "#0057FF"}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <DiamondWhite size="sm" />
                    <span style={{ fontFamily: "'Work Sans', sans-serif", fontWeight: "700", fontSize: "11px", color: "#FFFFFF" }}>Begin Session</span>
                  </div>
                  <span style={{ fontFamily: "'Geist Mono', 'IBM Plex Mono', monospace", fontSize: "6.5px", color: "#AACCFF", letterSpacing: "0.08em" }}>FREE — AI FACILITATED</span>
                </button>
                <div className="bg-cta-secondary" style={{ background: "#FFFFFF", border: "0.5px solid #DDDDDD", borderLeft: "none", padding: "20px 24px", fontFamily: "'Work Sans', sans-serif", fontSize: "11px", color: "#444444", display: "flex", alignItems: "center", lineHeight: "1.5" }}>
                  7 gates. One decision. One rule.
                </div>
              </div>

              <hr style={{ border: "none", borderTop: "0.5px solid #DDDDDD", marginTop: "48px" }} />
            </div>
          </div>
        ) : (
          /* SESSION */
          <div className="bg-session" style={{ width: "100%", maxWidth: "700px", height: "100vh", display: "flex", flexDirection: "column", background: "#FFFFFF" }}>
            {/* Header */}
            <div className="bg-session-header" style={{ padding: "16px 24px", borderBottom: "1px solid #0F0F0F", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#FFFFFF", flexWrap: "wrap", gap: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <DiamondBlue size="md" />
                <span style={{ fontFamily: "'Geist Mono', 'IBM Plex Mono', monospace", fontSize: "6.5px", textTransform: "uppercase", letterSpacing: "0.1em", color: "#888888" }}>BINARY GAME</span>
                <span className="hidden-mobile" style={{ fontFamily: "'Geist Mono', 'IBM Plex Mono', monospace", fontSize: "6.5px", textTransform: "uppercase", letterSpacing: "0.1em", color: "#DDDDDD", marginLeft: "4px" }}>| ACTIVE SESSION</span>
              </div>
              <button
                onClick={handleReset}
                style={{ background: "none", border: "0.5px solid #DDDDDD", padding: "5px 12px", fontFamily: "'Geist Mono', 'IBM Plex Mono', monospace", fontSize: "6.5px", textTransform: "uppercase", letterSpacing: "0.1em", color: "#888888", cursor: "pointer" }}
                onMouseOver={e => e.currentTarget.style.borderColor = "#0057FF"}
                onMouseOut={e => e.currentTarget.style.borderColor = "#DDDDDD"}
              >RESET</button>
            </div>

            <GateTracker activeGate={activeGate} />
            <div className="bg-messages" style={{ flex: 1, overflowY: "auto", padding: "32px 24px 16px", background: "#FFFFFF" }}>
              {messages.map((msg, i) =>
                msg.role === "assistant" ? <GateCard key={i} msg={msg} /> : <UserCard key={i} msg={msg} />
              )}
              {loading && <TypingIndicator />}
              <ResultCard visible={showResult} />
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div style={{ borderTop: "0.5px solid #DDDDDD", background: "#FAFAF8" }}>
              <div className="bg-input-area" style={{ display: "flex", alignItems: "flex-end", padding: "12px 16px", gap: "8px" }}>
                <div
                  style={{ flex: 1, background: "#FFFFFF", border: "0.5px solid #DDDDDD", display: "flex", alignItems: "flex-end", transition: "border-color 0.15s", minWidth: 0 }}
                  id="input-wrap"
                >
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your response..."
                    rows={1}
                    style={{ flex: 1, background: "transparent", border: "none", padding: "12px 14px", color: "#0F0F0F", fontSize: "16px", fontFamily: "'Work Sans', sans-serif", lineHeight: "1.5", maxHeight: "100px", overflowY: "auto", minWidth: 0, width: "100%" }}
                    onFocus={e => { document.getElementById("input-wrap").style.borderColor = "#0057FF"; }}
                    onBlur={e => { document.getElementById("input-wrap").style.borderColor = "#DDDDDD"; }}
                  />
                </div>
                <button
                  onClick={() => sendMessage(input)}
                  disabled={loading || !input.trim()}
                  style={{
                    background: input.trim() && !loading ? "#0057FF" : "#F0F0EE",
                    border: "none", padding: "0 16px", height: "44px",
                    color: input.trim() && !loading ? "#FFFFFF" : "#BBBBBB",
                    cursor: input.trim() && !loading ? "pointer" : "not-allowed",
                    fontFamily: "'Geist Mono', 'IBM Plex Mono', monospace", fontSize: "8px", fontWeight: "700",
                    letterSpacing: "0.1em", textTransform: "uppercase",
                    display: "flex", alignItems: "center", gap: "6px",
                    transition: "background 0.15s ease", flexShrink: 0,
                  }}
                  onMouseOver={e => { if (input.trim() && !loading) e.currentTarget.style.background = "#0044DD"; }}
                  onMouseOut={e => { if (input.trim() && !loading) e.currentTarget.style.background = "#0057FF"; }}
                >
                  {input.trim() && !loading && <DiamondWhite size="sm" />}
                  SEND
                </button>
              </div>
              <div className="bg-input-hint" style={{ padding: "0 16px 10px", fontFamily: "'Geist Mono', 'IBM Plex Mono', monospace", fontSize: "6.5px", color: "#CCCCCC", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                ENTER TO SEND — SHIFT + ENTER FOR NEW LINE
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
